import { useEffect, useRef, useState, useCallback } from 'react'

import { UploadSelfie as BLUploadSelfie } from './../../../utilities/BL/SelfieFan'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage' // asumiendo tu hook


// -----------------------------------------------------------------------------
// SelfieFan.jsx
// Componente para capturar una selfie desde la cámara, previsualizar, rotar,
// y subirla con metadata { source: "selfie_fan" }.
// Incluye "cooldown" para permitir otra subida hasta pasados X ms.
// -----------------------------------------------------------------------------

// Clave para persistir el cooldown entre recargas
const COOLDOWN_STORAGE_KEY = "selfiefan_next_allowed_ts";

// Utilidad: Formatea ms → "m:ss"
function msToMinSec(ms) {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Espera a que el <video> tenga dimensiones reales (videoWidth/Height > 0).
 * Usa eventos + polling + timeout por seguridad.
 */
function waitForVideoReady(video, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!video) return reject(new Error("Referencia <video> nula"));

    if (video.videoWidth > 0 && video.videoHeight > 0) {
      return resolve();
    }

    let done = false;

    const onReady = () => {
      if (done) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        done = true;
        cleanup();
        resolve();
      }
    };

    const onLoadedMetadata = () => onReady();
    const onCanPlay = () => onReady();

    const poll = () => {
      if (done) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        onReady();
      } else {
        requestAnimationFrame(poll);
      }
    };

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("Timeout esperando a que la cámara esté lista"));
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("canplay", onCanPlay);
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("canplay", onCanPlay);
    poll();
  });
}

/**
 * Componente SelfieFan
 *
 * Vistas:
 * - 'intro'   : instrucciones + botón "Iniciar cámara"
 * - 'camera'  : cámara a pantalla completa + botón "Tomar foto"
 * - 'preview' : vista previa + girar/restaurar + elegir mensaje + enviar
 * - 'sent'    : confirmación + cooldown con contador
 */
const SelfieFan = ({ cooldownMs = 2 * 60 * 1000 /* default 2 minutos */ }) => {
  const { showMessage } = useFloatMessage();

  // --- Refs DOM ---
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewUrlRef = useRef(null);

  // --- Control de vistas ---
  const [view, setView] = useState("intro"); // 'intro' | 'camera' | 'preview' | 'sent'

  // --- Estados de cámara/subida ---
  const mediaStreamRef = useRef(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // --- Imagen/preview/rotación ---
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lastBlob, setLastBlob] = useState(null); // lo que realmente se sube
  const [baseBlob, setBaseBlob] = useState(null); // blob base para giros
  const baseBitmapRef = useRef(null); // ImageBitmap o HTMLImageElement
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270

  // --- Mensajes de apoyo ---
  const supportMessages = { ONE: 1, TWO: 2, THREE: 3 };
  const SUPPORT_MESSAGE_BY_ID = {
    [supportMessages.ONE]: "Vamos con todo equipo",
    [supportMessages.TWO]: "Viva el Xolos",
    [supportMessages.THREE]: "Arriba el Xolos",
  };
  const DEFAULT_SUPPORT_MESSAGE = SUPPORT_MESSAGE_BY_ID[supportMessages.ONE];
  const [supportMessageSelected, setSupportMessageSelected] = useState(0);

  // --- Límites de tamaño para controlar el archivo ---
  const MAX_WIDTH = 1280;
  const MAX_HEIGHT = 1280;

  // --- Cooldown (próxima vez permitida) ---
  const [nextAllowedTs, setNextAllowedTs] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Cargar cooldown persistido al montar
  useEffect(() => {
    const saved = localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (saved) {
      const ts = parseInt(saved, 10);
      if (!Number.isNaN(ts)) setNextAllowedTs(ts);
    }
  }, []);

  // Tick de countdown
  useEffect(() => {
    const tick = () => {
      if (!nextAllowedTs) {
        setRemainingMs(0);
        return;
      }
      const diff = nextAllowedTs - Date.now();
      setRemainingMs(Math.max(0, diff));
    };

    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [nextAllowedTs]);

  const canStartNew = !nextAllowedTs || Date.now() >= nextAllowedTs;

  // --- Helpers de imagen ---
  async function ensureBaseBitmapFromBlob(blob) {
    if (!blob) return;
    try {
      if ("createImageBitmap" in window) {
        baseBitmapRef.current = await createImageBitmap(blob);
        return;
      }
    } catch {
      // fallback continúa
    }

    const objUrl = URL.createObjectURL(blob);
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        baseBitmapRef.current = img;
        URL.revokeObjectURL(objUrl);
        resolve();
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(objUrl);
        reject(e);
      };
      img.src = objUrl;
    });
  }

  /**
   * Renderiza desde el bitmap base → canvas aplicando la rotación,
   * actualiza el preview y el blob final (lastBlob).
   */
  async function renderFromBaseAndUpdate(rotationDeg) {
    const bmp = baseBitmapRef.current;
    if (!bmp || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const srcW = bmp.width;
    const srcH = bmp.height;

    const isQuarterTurn = rotationDeg % 180 !== 0;
    const outW = isQuarterTurn ? srcH : srcW;
    const outH = isQuarterTurn ? srcW : srcH;

    canvas.width = outW;
    canvas.height = outH;

    ctx.save();
    switch (rotationDeg % 360) {
      case 0:
        break;
      case 90:
        ctx.translate(outW, 0);
        ctx.rotate(Math.PI / 2);
        break;
      case 180:
        ctx.translate(outW, outH);
        ctx.rotate(Math.PI);
        break;
      case 270:
        ctx.translate(0, outH);
        ctx.rotate((3 * Math.PI) / 2);
        break;
      default:
        break;
    }

    ctx.drawImage(bmp, 0, 0);
    ctx.restore();

    const rotatedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen rotada"))),
        "image/jpeg",
        0.92
      );
    });

    const objUrl = URL.createObjectURL(rotatedBlob);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = objUrl;
    setPreviewUrl(objUrl);
    setLastBlob(rotatedBlob);
  }

  // --- Cámara: iniciar / detener ---
  const startCamera = useCallback(async () => {
    // Si sigue en cooldown, no dejamos iniciar
    if (!canStartNew) {
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: `Podrás subir otra foto en ${msToMinSec(nextAllowedTs - Date.now())}.`,
      });
      return;
    }

    setErrorMsg(null);
    setIsStarting(true);
    setIsVideoReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" } },
        audio: false,
      });
      mediaStreamRef.current = stream;

      // Primero montamos la vista cámara para que el <video> exista en el DOM
      setView("camera");
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text:
          "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos.",
      });
      setErrorMsg("No ha sido posible acceder a la cámara: " + err.message);
    } finally {
      setIsStarting(false);
    }
  }, [canStartNew, nextAllowedTs, showMessage]);

  const stopCamera = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  // Cuando la vista es "camera", conectamos el stream al <video> y esperamos a que esté listo
  useEffect(() => {
    if (view !== "camera") return;

    const v = videoRef.current;
    const stream = mediaStreamRef.current;
    if (!v || !stream) return;

    v.srcObject = stream;
    v.muted = true;
    v.setAttribute("playsinline", "true");

    let cancelled = false;

    (async () => {
      try {
        try {
          await v.play();
        } catch {
          // algunos navegadores requieren interacción del usuario; el botón se mantendrá deshabilitado hasta ready
        }
        await waitForVideoReady(v, 8000);
        if (!cancelled) setIsVideoReady(true);
      } catch (e) {
        if (cancelled) return;
        setIsVideoReady(false);
        setErrorMsg("No se pudo preparar el video: " + (e?.message ?? ""));
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "No se pudo preparar la cámara. Intenta de nuevo.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [view, showMessage]);

  // Cleanup global al desmontar
  useEffect(() => {
    return () => {
      stopCamera();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [stopCamera]);

  // --- Capturar frame del video a canvas ---
  const handleCapture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Si por alguna razón aún no está listo, esperamos un poco más
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      try {
        await waitForVideoReady(video, 3000);
      } catch {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: "La cámara aún no está lista. Espera un momento e inténtalo de nuevo.",
        });
        return;
      }
    }

    setIsCapturing(true);
    setErrorMsg(null);

    try {
      const ctx = canvas.getContext("2d");

      const vw = video.videoWidth;
      const vh = video.videoHeight;

      // Reducción proporcional para no generar archivos gigantes
      let targetW = vw;
      let targetH = vh;
      const ratio = vw / vh;
      if (targetW > MAX_WIDTH) {
        targetW = MAX_WIDTH;
        targetH = Math.round(MAX_WIDTH / ratio);
      }
      if (targetH > MAX_HEIGHT) {
        targetH = MAX_HEIGHT;
        targetW = Math.round(MAX_HEIGHT * ratio);
      }

      canvas.width = targetW;
      canvas.height = targetH;

      // Espejo para selfie (si no lo quieres, comenta translate/scale y el restore)
      ctx.save();
      ctx.translate(targetW, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, targetW, targetH);
      ctx.restore();

      // Blob base de la captura
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) =>
            b
              ? resolve(b)
              : reject(new Error("Error al generar la vista previa. Inténtalo de nuevo.")),
          "image/jpeg",
          0.92
        );
      });

      // Preparar preview inicial (rotación 0)
      setBaseBlob(blob);
      await ensureBaseBitmapFromBlob(blob);
      setRotation(0);
      await renderFromBaseAndUpdate(0);

      // Cerrar cámara y pasar a PREVIEW
      stopCamera();
      setView("preview");
    } catch (err) {
      console.error(err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: err.message || "No se pudo capturar la foto correctamente.",
      });
      setErrorMsg(err.message || "No se pudo capturar la foto correctamente.");
    } finally {
      setIsCapturing(false);
    }
  }, [MAX_HEIGHT, MAX_WIDTH, showMessage, stopCamera]);

  // --- Subir imagen final ---
  const handleUpload = useCallback(async () => {
    if (!lastBlob) {
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: "¡Ups! Debes tomar una foto antes de enviarla.",
      });
      setErrorMsg("Debes tomar una foto antes de enviarla.");
      return;
    }

    setIsUploading(true);
    setErrorMsg(null);

    try {
      const supportMessage = SUPPORT_MESSAGE_BY_ID[supportMessageSelected] || DEFAULT_SUPPORT_MESSAGE;

      // Payload con metadata requerida
      const payload = {
        mode: "binary",           // o "multipart" según tu DL
        blob: lastBlob,           // el archivo real
        caption: supportMessage,  // opcional
        source: "selfie_fan",     // <<<<<<<<<<<<<<<<<<<<<<<< METADATA
      };

      const response = await BLUploadSelfie(payload);

      if (response?.success) {
        // Fijar el cooldown (persistente)
        const ts = Date.now() + cooldownMs;
        setNextAllowedTs(ts);
        localStorage.setItem(COOLDOWN_STORAGE_KEY, String(ts));

        // Ir a ENVIADO
        setView("sent");

        // Limpieza mínima de estado crítico
        setSupportMessageSelected(0);
      } else {
        throw new Error(response?.message || "Error desconocido al subir.");
      }
    } catch (err) {
      console.error(err);
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text:
          "Lo sentimos. Hubo un error al enviar tu foto. Inténtalo nuevamente más tarde.",
      });
      setErrorMsg(err.message || "Error al enviar la foto.");
    } finally {
      setIsUploading(false);
    }
  }, [lastBlob, supportMessageSelected, cooldownMs, showMessage]);

  // --- UI ---
  return (
    <>
      <div className="container SelfieFan">
        {/* INTRO: Instrucciones + iniciar cámara */}
        {view === "intro" && (
          <div className="SelfieFan__Intro container">
            <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" />
            <p className="SelfieFan__Fan__Title">Bienvenid@ Aficionad@</p>
            <p className="SelfieFan__Fan__Text--Center">¡Apoyemos a nuestro equipo!</p>

            <ol className="SelfieFan__List">
              <li>Tómate una selfie</li>
              {/* <li>Elige el mensaje de apoyo que más te guste</li> */}
              <li>Da clic en “Enviar mi foto”</li>
            </ol>

            <p className="SelfieFan__Fan__Text--Center">
              Tu foto podría aparecer en pantalla mostrando tu apoyo al equipo
            </p>

            <div className="SelfieFan__Intro__Actions">
              <button
                className="Btn__Adsl--Red"
                onClick={startCamera}
                disabled={isStarting || !canStartNew}
                title={!canStartNew ? `Puedes tomar otra en ${msToMinSec(remainingMs)}` : ""}
              >
                {isStarting
                  ? "Abriendo cámara..."
                  : canStartNew
                  ? (
                    <>
                      <span className="bi bi-camera" /> Iniciar cámara
                    </>
                  )
                  : `Puedes tomar otra en ${msToMinSec(remainingMs)}`}
              </button>
            </div>
          </div>
        )}

        {/* CÁMARA: overlay pantalla completa + botón flotante "Tomar foto" */}
        {view === "camera" && (
          <div className="SelfieFan__Overlay">
            <div className="SelfieFan__Overlay__VideoWrapper">
              <video
                ref={videoRef}
                className="SelfieFan__Overlay__Video"
                autoPlay
                playsInline
                muted
              />
              <button
                className="SelfieFan__Overlay__CaptureBtn Btn__Adsl--Red"
                onClick={handleCapture}
                disabled={isCapturing || !isVideoReady}
              >
                {isCapturing
                  ? "Capturando..."
                  : isVideoReady
                  ? (
                    <>
                      <span className="bi bi-camera" /> Tomar foto
                    </>
                  )
                  : "Preparando cámara..."}
              </button>
            </div>
          </div>
        )}

        {/* Canvas oculto para procesar imagen */}
        <canvas ref={canvasRef} className="d-none" />

        {/* PREVIEW: imagen + girar + elegir mensaje + enviar */}
        {view === "preview" && (
          <div className="SelfieFan__Preview container">
            <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" />
            <p className="text-center">Vista previa:</p>

            {/* Acciones principales de preview */}
            <div className="SelfieFan__Preview__RotateActions">
              <button
                type="button"
                className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
                disabled={!baseBlob || isCapturing}
                onClick={async () => {
                  const newRot = (rotation + 90) % 360;
                  setRotation(newRot);
                  await renderFromBaseAndUpdate(newRot);
                }}
              >
                <i className="bi bi-arrow-clockwise" /> Girar
              </button>

              <button
                className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
                onClick={async () => {
                  // Limpiar preview y volver a cámara (no aplica cooldown hasta que se envíe)
                  setPreviewUrl(null);
                  if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = null;
                  }
                  setBaseBlob(null);
                  setLastBlob(null);
                  setRotation(0);
                  await startCamera();
                }}
                disabled={isStarting}
              >
                <i className="bi bi-camera" /> Tomar otra
              </button>
            </div>

            {previewUrl && (
              <img
                src={previewUrl}
                alt="Tu foto aparecerá aquí"
                className="SelfieFan__Preview__Image"
              />
            )}

            {/* Enviar */}
            <div className="SelfieFan__Preview__Send">
              <button
                onClick={handleUpload}
                className="Btn__Adsl--Red"
                disabled={!lastBlob || isUploading}
              >
                {isUploading ? "Subiendo foto..." : "Enviar mi foto"}
              </button>
            </div>

            {errorMsg && (
              <p className="text-center mt-4" style={{ color: "crimson" }}>
                <strong>Error: </strong> {errorMsg}
              </p>
            )}
          </div>
        )}

        {/* ENVIADO: confirmación + botón bloqueado hasta fin del cooldown */}
        {view === "sent" && (
          <div className="SelfieFan__Sent container">
            <img src="/img/page/xolos-five-bicolor.png" className="LogoADSLxZtudia" />
            <p className="fs-1 mb-0 text-success">
              <i className="bi bi-check-circle-fill" />
            </p>
            <p className="SelfieFan__Sent__Title">¡Gracias por tu apoyo!</p>
            <p className="SelfieFan__Fan__Text--Center">
              Tu foto ha sido enviada correctamente
            </p>

            <div className="SelfieFan__Sent__Actions">
              <button
                className="Btn__Adsl--RedOutline mx-auto"
                onClick={() => {
                  if (!canStartNew) return; // seguridad extra

                  // Reiniciar flujo (estado de imagen)
                  setPreviewUrl(null);
                  if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                    previewUrlRef.current = null;
                  }
                  setBaseBlob(null);
                  setLastBlob(null);
                  setRotation(0);
                  setErrorMsg(null);
                  setSupportMessageSelected(0);

                  // Volver a intro
                  setView("intro");
                }}
                disabled={!canStartNew}
                title={!canStartNew ? `Puedes tomar otra foto en ${msToMinSec(remainingMs)}` : ""}
              >
                {canStartNew ? "Tomar otra foto" : `Puedes tomar otra foto en ${msToMinSec(remainingMs)}`}
              </button>
            </div>

            <hr />

            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' className='SelfieFan__Sent__Banner'>
              <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Certifícate con Ztudia' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
            </a>
            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner&utm_campaign=fdm_xolos_s2' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

            <hr />

            <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
            <p className='text-center mb-0'>Interaktia está listo para ti</p>
            <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=banner_text&utm_campaign=fdm_xolos_s2' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
          </div>
        )}
      </div>

      <footer className="text-center mt-3 FooterCredits">
        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=selfiefan_web&utm_medium=footer&utm_campaign=fdm_xolos_s2' target='_blank' className='text-dark'>Interaktia</a> | 2026
      </footer>
    </>
  );
};

export { SelfieFan }

















// /**
//  * Componente SelfieFan (flujo por estados)
//  * Estados de vista:
//  * - 'intro'   : Instrucciones + botón Iniciar cámara
//  * - 'camera'  : Cámara a pantalla completa + botón flotante "Tomar foto"
//  * - 'preview' : Vista previa + rotación + mensajes + enviar
//  * - 'sent'    : Mensaje final
//  */
// const SelfieFan = () => {
//   const { showMessage } = useFloatMessage();

//   const videoRef = useRef(null);
//   const canvasRef = useRef(null);

//   // Control de la UI
//   const [view, setView] = useState("intro"); // 'intro' | 'camera' | 'preview' | 'sent'

//   // Estados existentes
//   const [previewUrl, setPreviewUrl] = useState(null);
//   const previewUrlRef = useRef(null);

//   const [isStarting, setIsStarting] = useState(false);
//   const [isCapturing, setIsCapturing] = useState(false);
//   const [isUploading, setIsUploading] = useState(false);

//   const [selfieFanShooted, setSelfieFanShooted] = useState(false);
//   const [supportMessageSelected, setSupportMessageSelected] = useState(0);
//   const [errorMsg, setErrorMsg] = useState(null);

//   // Mensajes disponibles
//   const supportMessages = { ONE: 1, TWO: 2, THREE: 3 };
//   const SUPPORT_MESSAGE_BY_ID = {
//     [supportMessages.ONE]: "Vamos con todo equipo",
//     [supportMessages.TWO]: "Viva el San Luis",
//     [supportMessages.THREE]: "Arriba las Auriazules",
//   };
//   const DEFAULT_SUPPORT_MESSAGE = SUPPORT_MESSAGE_BY_ID[supportMessages.ONE];

//   // Límites de tamaño
//   const MAX_WIDTH = 1280;
//   const MAX_HEIGHT = 1280;

//   // Para subir exactamente lo que se ve en la vista previa
//   const [lastBlob, setLastBlob] = useState(null);

//   // Rotación
//   const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270
//   const [baseBlob, setBaseBlob] = useState(null);
//   const baseBitmapRef = useRef(null);

//   // --- Helpers de imagen ---
//   async function ensureBaseBitmapFromBlob(blob) {
//     if (!blob) return;
//     try {
//       if ("createImageBitmap" in window) {
//         baseBitmapRef.current = await createImageBitmap(blob);
//         return;
//       }
//     } catch {
//       // fallback continúa
//     }

//     const objUrl = URL.createObjectURL(blob);
//     await new Promise((resolve, reject) => {
//       const img = new Image();
//       img.onload = () => {
//         baseBitmapRef.current = img;
//         URL.revokeObjectURL(objUrl);
//         resolve();
//       };
//       img.onerror = (e) => {
//         URL.revokeObjectURL(objUrl);
//         reject(e);
//       };
//       img.src = objUrl;
//     });
//   }

//   async function renderFromBaseAndUpdate(rotationDeg) {
//     const bmp = baseBitmapRef.current;
//     if (!bmp || !canvasRef.current) return;

//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext("2d");

//     const srcW = bmp.width;
//     const srcH = bmp.height;

//     const isQuarterTurn = rotationDeg % 180 !== 0;
//     const outW = isQuarterTurn ? srcH : srcW;
//     const outH = isQuarterTurn ? srcW : srcH;

//     canvas.width = outW;
//     canvas.height = outH;

//     ctx.save();
//     switch (rotationDeg % 360) {
//       case 0:
//         break;
//       case 90:
//         ctx.translate(outW, 0);
//         ctx.rotate(Math.PI / 2);
//         break;
//       case 180:
//         ctx.translate(outW, outH);
//         ctx.rotate(Math.PI);
//         break;
//       case 270:
//         ctx.translate(0, outH);
//         ctx.rotate((3 * Math.PI) / 2);
//         break;
//       default:
//         break;
//     }

//     ctx.drawImage(bmp, 0, 0);
//     ctx.restore();

//     const rotatedBlob = await new Promise((resolve, reject) => {
//       canvas.toBlob(
//         (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen rotada"))),
//         "image/jpeg",
//         0.92
//       );
//     });

//     const objUrl = URL.createObjectURL(rotatedBlob);
//     if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
//     previewUrlRef.current = objUrl;
//     setPreviewUrl(objUrl);
//     setLastBlob(rotatedBlob);
//   }

//   const SelectSupportMessage = async (type) => {
//     try {
//       setSupportMessageSelected(type);
//     } catch {
//       /* noop */
//     }
//   };

//   // --- Cámara: iniciar/limpiar explícitamente por botón ---
//   const mediaStreamRef = useRef(null);
//   const [isVideoReady, setIsVideoReady] = useState(false);

//   // const startCamera = useCallback(async () => {
//   //   setErrorMsg(null);
//   //   setIsStarting(true);
//   //   setIsVideoReady(false);
//   //   try {
//   //     const stream = await navigator.mediaDevices.getUserMedia({
//   //       video: { facingMode: { ideal: "user" } }, // más tolerante que "user" a secas
//   //       audio: false,
//   //     });
//   //     mediaStreamRef.current = stream;

//   //     // if (videoRef.current) {
//   //     //   videoRef.current.srcObject = stream;
//   //     //   videoRef.current.muted = true;
//   //     //   await videoRef.current.play();
//   //     // }
//   //     // setView("camera");
//   //     const v = videoRef.current;
//   //     if (v) {
//   //       v.srcObject = stream;
//   //       v.muted = true;
//   //       // iOS requiere esto
//   //       v.setAttribute("playsinline", "true");
//   //       try { await v.play(); } catch { /* algunos navegadores requieren un gesto extra */ }

//   //       await waitForVideoReady(v);   // 👈 ESPERA AQUÍ
//   //       setIsVideoReady(true);
//   //       setView("camera");
//   //     }
//   //   } catch (err) {
//   //     console.error("Error al acceder a la cámara:", err);
//   //     showMessage({
//   //       type: MESSAGE_TYPE.BAD,
//   //       text:
//   //         "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos de tu navegador para darnos acceso.",
//   //     });
//   //     setErrorMsg(
//   //       "No ha sido posible acceder a la cámara. Autoriza permisos. Detalle: " + err.message
//   //     );
//   //   } finally {
//   //     setIsStarting(false);
//   //   }
//   // }, [showMessage]);

//   const startCamera = useCallback(async () => {
//     setErrorMsg(null);
//     setIsStarting(true);
//     setIsVideoReady(false);
//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({
//         video: { facingMode: { ideal: "user" } },
//         audio: false,
//       });
//       mediaStreamRef.current = stream;

//       // 👉 Cambia la vista *ya*, para que el <video> exista en el DOM.
//       setView("camera");
//     } catch (err) {
//       console.error("Error al acceder a la cámara:", err);
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text:
//           "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos.",
//       });
//       setErrorMsg("No ha sido posible acceder a la cámara: " + err.message);
//     } finally {
//       setIsStarting(false);
//     }
//   }, [showMessage]);

//   useEffect(() => {
//     //document.body.classList.add('BodyComplete')

//     if (view !== "camera") return;

//     const v = videoRef.current;
//     const stream = mediaStreamRef.current;

//     if (!v || !stream) return;

//     // Conectar stream al video
//     v.srcObject = stream;
//     v.muted = true;
//     v.setAttribute("playsinline", "true");

//     let cancelled = false;

//     (async () => {
//       try {
//         // Espera a que realmente se pueda reproducir
//         try { await v.play(); } catch { /* algunos navegadores requieren gesto */ }
//         await waitForVideoReady(v, 8000);
//         if (!cancelled) setIsVideoReady(true);
//       } catch (e) {
//         if (cancelled) return;
//         setIsVideoReady(false);
//         setErrorMsg("No se pudo preparar el video: " + (e?.message ?? ""));
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: "No se pudo preparar la cámara. Intenta de nuevo.",
//         });
//       }
//     })();

//     return () => {
//       // No detenemos el stream aquí; solo limpiamos flags si cambia la vista
//       cancelled = true;
//     };
//   }, [view, showMessage]);

//   const stopCamera = useCallback(() => {
//     const stream = mediaStreamRef.current;
//     if (stream) {
//       stream.getTracks().forEach((t) => t.stop());
//       mediaStreamRef.current = null;
//     }
//   }, []);

//   useEffect(() => {
//     // Cleanup al desmontar
//     return () => {
//       stopCamera();
//       if (previewUrlRef.current) {
//         URL.revokeObjectURL(previewUrlRef.current);
//         previewUrlRef.current = null;
//       }
//     };
//   }, [stopCamera]);

//   // --- Capturar ---
//   const handleCapture = useCallback(async () => {
//     try {
//       const video = videoRef.current;
//       const canvas = canvasRef.current;
//       if (!video || !canvas) return;

//       //if (!videoRef.current || !canvasRef.current) return;

//       // Si por alguna razón no está listo, intentamos esperar de nuevo brevemente
//       if (video.videoWidth === 0 || video.videoHeight === 0) {
//         try {
//           await waitForVideoReady(video, 3000); // 3s extra
//         } catch {
//           showMessage({
//             type: MESSAGE_TYPE.BAD,
//             text: "La cámara aún no está lista. Espera un momento e inténtalo de nuevo.",
//           });
//           return;
//         }
//       }

//       setSelfieFanShooted(false);
//       setIsCapturing(true);
//       setErrorMsg(null);

//       // const video = videoRef.current;
//       // const canvas = canvasRef.current;
//       const ctx = canvas.getContext("2d");

//       const vw = video.videoWidth;
//       const vh = video.videoHeight;
//       if (!vw || !vh) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text:
//             "La cámara aún no está lista para capturar. Acepta permisos o espera un momento.",
//         });
//         throw new Error("Cámara no lista para capturar.");
//       }

//       let targetW = vw;
//       let targetH = vh;
//       const ratio = vw / vh;
//       if (targetW > MAX_WIDTH) {
//         targetW = MAX_WIDTH;
//         targetH = Math.round(MAX_WIDTH / ratio);
//       }
//       if (targetH > MAX_HEIGHT) {
//         targetH = MAX_HEIGHT;
//         targetW = Math.round(MAX_HEIGHT * ratio);
//       }

//       canvas.width = targetW;
//       canvas.height = targetH;

//       // Espejo visual en foto (si no lo quieres, comenta las 3 líneas save/translate/scale y restore)
//       ctx.save();
//       ctx.translate(targetW, 0);
//       ctx.scale(-1, 1);
//       ctx.drawImage(video, 0, 0, targetW, targetH);
//       ctx.restore();

//       const blob = await new Promise((resolve, reject) => {
//         canvas.toBlob(
//           (b) =>
//             b
//               ? resolve(b)
//               : reject(new Error("Error al generar la vista previa. Inténtalo de nuevo.")),
//           "image/jpeg",
//           0.92
//         );
//       });

//       // Guardar base y preparar preview sin cerrar aún la cámara
//       setBaseBlob(blob);
//       await ensureBaseBitmapFromBlob(blob);
//       setRotation(0);
//       await renderFromBaseAndUpdate(0);

//       setSelfieFanShooted(true);

//       // Ahora sí, cerrar cámara y pasar a 'preview'
//       stopCamera();
//       setView("preview");
//     } catch (err) {
//       console.error(err);
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: err.message || "No se pudo capturar la foto correctamente.",
//       });
//     } finally {
//       setIsCapturing(false);
//     }
//   }, [MAX_HEIGHT, MAX_WIDTH, renderFromBaseAndUpdate, showMessage, stopCamera]);

//   // --- Subir ---
//   const handleUpload = useCallback(async () => {
//     if (!lastBlob) {
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: "¡Ups! Debes tomar una foto antes de enviarla.",
//       });
//       setErrorMsg("Debes tomar una foto antes de enviarla.");
//       return;
//     }
//     setIsUploading(true);
//     setErrorMsg(null);

//     try {
//       const supportMessage =
//         SUPPORT_MESSAGE_BY_ID[supportMessageSelected] || DEFAULT_SUPPORT_MESSAGE;

//       const selfieFanData = {
//         blob: lastBlob,
//         mode: "binary", // "multipart" | "binary"
//         caption: supportMessage,
//       };

//       const response = await BLUploadSelfie(selfieFanData);

//       if (response?.success) {
//         // showMessage({
//         //   type: MESSAGE_TYPE.SUCCESS,
//         //   text: "¡Muchas gracias! Tu foto Selfie Fan ha sido enviada con éxito",
//         // });
//         // Limpiar un poco el estado y mostrar pantalla final
//         setView("sent");
//         setSelfieFanShooted(false);
//       } else {
//         console.log(response?.message);
//         throw new Error(response?.message || "Error desconocido al subir.");
//       }
//     } catch (err) {
//       console.error(err);
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text:
//           "Lo sentimos. Hubo un error al enviar tu foto. Inténtalo nuevamente más tarde.",
//       });
//       setErrorMsg(
//         err.message ||
//           "Lo sentimos. Hubo un error al enviar tu foto. Inténtalo más tarde."
//       );
//     } finally {
//       setIsUploading(false);
//     }
//   }, [lastBlob, supportMessageSelected, showMessage]);

//   function waitForVideoReady(video, timeoutMs = 8000) {
//     return new Promise((resolve, reject) => {
//       if (!video) return reject(new Error("video ref nulo"));

//       // Si ya está listo, resuelve inmediato
//       if (video.videoWidth > 0 && video.videoHeight > 0) {
//         return resolve();
//       }

//       let done = false;
//       const onReady = () => {
//         if (done) return;
//         if (video.videoWidth > 0 && video.videoHeight > 0) {
//           done = true;
//           cleanup();
//           resolve();
//         }
//       };

//       const onLoadedMetadata = () => onReady();
//       const onCanPlay = () => onReady();

//       const poll = () => {
//         if (done) return;
//         if (video.videoWidth > 0 && video.videoHeight > 0) {
//           onReady();
//         } else {
//           requestAnimationFrame(poll);
//         }
//       };

//       const timer = setTimeout(() => {
//         if (done) return;
//         done = true;
//         cleanup();
//         reject(new Error("Timeout esperando a que la cámara esté lista"));
//       }, timeoutMs);

//       const cleanup = () => {
//         clearTimeout(timer);
//         video.removeEventListener("loadedmetadata", onLoadedMetadata);
//         video.removeEventListener("canplay", onCanPlay);
//       };

//       video.addEventListener("loadedmetadata", onLoadedMetadata);
//       video.addEventListener("canplay", onCanPlay);
//       poll();
//     });
//   }

//   // --- UI ---
//   return (
//     <>
//       <div className="container SelfieFan">

//         {/* Estado: INTRO */}
//         {view === "intro" && (
//           <div className="SelfieFan__Intro container">
//             <img src='/img/page/xolos-five-bicolor.png' className='LogoADSLxZtudia' />
//             <p className="SelfieFan__Fan__Title">Bienvenid@ Aficionad@</p>
//             <p className="SelfieFan__Fan__Text--Center">¡Apoyemos a nuestro equipo!</p>

//             <ol className="SelfieFan__List">
//               <li>Tómate una Selfie Fan</li>
//               <li>Elige el mensaje de apoyo que más te guste</li>
//               <li>Da clic en “Enviar mi mensaje de apoyo”</li>
//             </ol>

//             <p className="SelfieFan__Fan__Text--Center">Tu Selfie Fan podría aparecer en pantalla mostrando tu apoyo al equipo</p>

//             <div className="SelfieFan__Intro__Actions">
//               <button className="Btn__Adsl--Red" onClick={startCamera} disabled={isStarting}>
//                 {isStarting ? "Abriendo cámara..." : <><span className='bi bi-camera'> Iniciar cámara</span></>}
//               </button>
//             </div>
//             {/* <p className='text-center'>Tip: toma la selfie horizontal <i className="bi bi-phone-landscape"></i></p> */}
//           </div>
//         )}

//         {/* Estado: CÁMARA a pantalla completa */}
//         {view === "camera" && (
//           <div className="SelfieFan__Overlay">
//             <div className="SelfieFan__Overlay__VideoWrapper">
//               <video
//                 ref={videoRef}
//                 className="SelfieFan__Overlay__Video"
//                 autoPlay
//                 playsInline
//                 muted
//               />
//               {/* <button
//                 className="SelfieFan__Overlay__CaptureBtn Btn__Adsl--Red"
//                 onClick={handleCapture}
//                 disabled={isCapturing}
//               >
//                 {isCapturing ? "Capturando..." : "Tomar foto"}
//               </button> */}
//               <button
//                 className="SelfieFan__Overlay__CaptureBtn Btn__Adsl--Red"
//                 onClick={handleCapture}
//                 disabled={isCapturing || !isVideoReady}   // 👈 deshabilitado hasta que esté listo
//               >
//                 {isCapturing ? "Capturando..." : (isVideoReady ? <><span className='bi bi-camera'> Tomar foto</span></> : "Preparando cámara...")}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* Canvas oculto para procesar imagen */}
//         <canvas ref={canvasRef} className="d-none" />

//         {/* Estado: PREVIEW */}
//         {view === "preview" && (
//           <div className="SelfieFan__Preview container">
//             <img src='/img/page/xolos-five-bicolor.png' className='LogoADSLxZtudia' />
//             <p className="text-center">Vista previa:</p>

//             <div className="SelfieFan__Preview__RotateActions">
//               <button
//                 type="button"
//                 className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
//                 disabled={!baseBlob || isCapturing}
//                 onClick={async () => {
//                   const newRot = (rotation + 90) % 360;
//                   setRotation(newRot);
//                   await renderFromBaseAndUpdate(newRot);
//                 }}
//               >
//                 <i className="bi bi-arrow-clockwise"></i> Girar
//               </button>
              
//               <button
//                 className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
//                 onClick={async () => {
//                   // Limpiar preview y volver a cámara
//                   setSelfieFanShooted(false);
//                   setPreviewUrl(null);
//                   if (previewUrlRef.current) {
//                     URL.revokeObjectURL(previewUrlRef.current);
//                     previewUrlRef.current = null;
//                   }
//                   setBaseBlob(null);
//                   setLastBlob(null);
//                   setRotation(0);
//                   await startCamera();
//                 }}
//                 disabled={isStarting}
//               >
//                 <i className="bi bi-camera"></i> Tomar otra
//               </button>
//             </div>

//             {previewUrl && (
//               <img
//                 src={previewUrl}
//                 alt="Tu foto aparecerá aquí"
//                 className="SelfieFan__Preview__Image"
//               />
//             )}

//             {/* Controles de giro */}
//             {/* <div className="SelfieFan__Preview__RotateActions">
//               <button
//                 type="button"
//                 className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
//                 disabled={!baseBlob || isCapturing}
//                 onClick={async () => {
//                   const newRot = (rotation + 90) % 360;
//                   setRotation(newRot);
//                   await renderFromBaseAndUpdate(newRot);
//                 }}
//               >
//                 <i className="bi bi-arrow-clockwise"></i> Girar
//               </button>

//               {/* <button
//                 type="button"
//                 className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
//                 disabled={!baseBlob || isCapturing || rotation === 0}
//                 onClick={async () => {
//                   setRotation(0);
//                   await renderFromBaseAndUpdate(0);
//                 }}
//               >
//                 Restaurar
//               </button> */}

//               {/*<button
//                 className="Btn__Adsl--RedOutline SelfieFan__Preview__RotateActions__RAButton"
//                 onClick={async () => {
//                   // Limpiar preview y volver a cámara
//                   setSelfieFanShooted(false);
//                   setPreviewUrl(null);
//                   if (previewUrlRef.current) {
//                     URL.revokeObjectURL(previewUrlRef.current);
//                     previewUrlRef.current = null;
//                   }
//                   setBaseBlob(null);
//                   setLastBlob(null);
//                   setRotation(0);
//                   await startCamera();
//                 }}
//                 disabled={isStarting}
//               >
//                 <i className="bi bi-camera"></i> Tomar otra
//               </button>
//             </div> */}

//             {/* Botón para repetir captura */}
//             {/* <div className="SelfieFan__Preview__Retake">
//               <button
//                 className="Btn__Adsl--RedOutline"
//                 onClick={async () => {
//                   // Limpiar preview y volver a cámara
//                   setSelfieFanShooted(false);
//                   setPreviewUrl(null);
//                   if (previewUrlRef.current) {
//                     URL.revokeObjectURL(previewUrlRef.current);
//                     previewUrlRef.current = null;
//                   }
//                   setBaseBlob(null);
//                   setLastBlob(null);
//                   setRotation(0);
//                   await startCamera();
//                 }}
//                 disabled={isStarting}
//               >
//                 <i className="bi bi-camera"></i> Tomar otra foto
//               </button>
//             </div> */}

//             <p className="SelfieFan__Fan__Title SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//               {supportMessageSelected
//                 ? SUPPORT_MESSAGE_BY_ID[supportMessageSelected]
//                 : DEFAULT_SUPPORT_MESSAGE}
//             </p>

//             {/* Mensajes de apoyo */}
//             <p className="SelfieFan__Fan__Video__SupportMessages__Title">Selecciona un mensaje de apoyo:</p>

//             <ul className="SelfieFan__Fan__Video__SupportMessages">
//               <li
//                 className={`${
//                   supportMessageSelected === supportMessages.ONE
//                     ? "Btn__Adsl--Red"
//                     : "Btn__Adsl--RedOutline"
//                 } SelfieFan__Fan__Video__SupportMessages__Item`}
//                 onClick={() => SelectSupportMessage(supportMessages.ONE)}
//               >
//                 Vamos con todo equipo
//               </li>
//               <li
//                 className={`${
//                   supportMessageSelected === supportMessages.TWO
//                     ? "Btn__Adsl--Red"
//                     : "Btn__Adsl--RedOutline"
//                 } SelfieFan__Fan__Video__SupportMessages__Item`}
//                 onClick={() => SelectSupportMessage(supportMessages.TWO)}
//               >
//                 Viva el San Luis
//               </li>
//               <li
//                 className={`${
//                   supportMessageSelected === supportMessages.THREE
//                     ? "Btn__Adsl--Red"
//                     : "Btn__Adsl--RedOutline"
//                 } SelfieFan__Fan__Video__SupportMessages__Item`}
//                 onClick={() => SelectSupportMessage(supportMessages.THREE)}
//               >
//                 Arriba las Auriazules
//               </li>
//             </ul>

//             {/* Texto debajo de la imagen */}
//             {/* <p className="SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//               {supportMessageSelected
//                 ? SUPPORT_MESSAGE_BY_ID[supportMessageSelected]
//                 : DEFAULT_SUPPORT_MESSAGE}
//             </p> */}

//             {/* Enviar */}
//             <div className="SelfieFan__Preview__Send">
//               <button
//                 onClick={handleUpload}
//                 className="Btn__Adsl--Red"
//                 disabled={!lastBlob || isUploading}
//               >
//                 {isUploading ? "Subiendo Selfie Fan..." : "Enviar mi mensaje de apoyo"}
//               </button>
//             </div>

//             {errorMsg && (
//               <p className="text-center mt-4" style={{ color: "crimson" }}>
//                 <strong>Error: </strong> {errorMsg}
//               </p>
//             )}
//           </div>
//         )}

//         {/* Estado: ENVIADO */}
//         {view === "sent" && (
//           <div className="SelfieFan__Sent container">
//             <img src='/img/page/xolos-five-bicolor.png' className='LogoADSLxZtudia' />
//             <p className='fs-1 mb-0 text-success'><i className="bi bi-check-circle-fill"></i></p>
//             <p className="SelfieFan__Sent__Title">¡Gracias por tu apoyo!</p>
//             <p className="SelfieFan__Fan__Text--Center">Tu selfie ha sido enviada correctamente</p>

//             <div className="SelfieFan__Sent__Actions">
//               <button
//                 className="Btn__Adsl--RedOutline"
//                 onClick={() => {
//                   // Reiniciar flujo si lo deseas:
//                   setPreviewUrl(null);
//                   if (previewUrlRef.current) {
//                     URL.revokeObjectURL(previewUrlRef.current);
//                     previewUrlRef.current = null;
//                   }
//                   setSupportMessageSelected(0);
//                   setBaseBlob(null);
//                   setLastBlob(null);
//                   setRotation(0);
//                   setErrorMsg(null);
//                   setView("intro");
//                 }}
//               >
//                 Tomar otra selfie
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       <footer className='text-center mt-3'>Hecho con ♥ por Interaktia | 2026</footer>
//     </>
//   );
// };

// export { SelfieFan }






























// import { useEffect, useRef, useState, useCallback } from 'react'

// import { UploadSelfie as BLUploadSelfie } from './../../../utilities/BL/SelfieFan'

// import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'


// /**
//  * Componente SelfieFan
//  * - Muestra la cámara frontal
//  * - Permite capturar una foto como Blob (sin escribir a disco)
//  * - Previsualiza la foto
//  * - Rotar la imagen en pasos de 90°
//  * - Sube a una API mediante la capa BL/DL
//  */
// const SelfieFan = () => {
//   const { showMessage } = useFloatMessage();

//   const videoRef = useRef(null);
//   const canvasRef = useRef(null);

//   const [previewUrl, setPreviewUrl] = useState(null);
//   const previewUrlRef = useRef(null); // para revocar el objectURL previo

//   const [isStarting, setIsStarting] = useState(true);
//   const [isCapturing, setIsCapturing] = useState(false);
//   const [isUploading, setIsUploading] = useState(false);

//   const [selfieFanShooted, setSelfieFanShooted] = useState(false);
//   const [supportMessageSelected, setSupportMessageSelected] = useState(0);

//   const [errorMsg, setErrorMsg] = useState(null);

//   // Mensajes disponibles
//   const supportMessages = { ONE: 1, TWO: 2, THREE: 3 };
//   const SUPPORT_MESSAGE_BY_ID = {
//     [supportMessages.ONE]: "Vamos con todo equipo",
//     [supportMessages.TWO]: "Viva el San Luis",
//     [supportMessages.THREE]: "Arriba las Auriazules",
//   };
//   const DEFAULT_SUPPORT_MESSAGE = SUPPORT_MESSAGE_BY_ID[supportMessages.ONE];

//   // Opcional: limitar resolución para controlar tamaño de archivo
//   const MAX_WIDTH = 1280;
//   const MAX_HEIGHT = 1280;

//   // Guardar el último blob capturado para subirlo (el que ves en preview)
//   const [lastBlob, setLastBlob] = useState(null);

//   // --- ROTACIÓN ---
//   const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270
//   const [baseBlob, setBaseBlob] = useState(null); // Blob original de la captura (antes de giros)
//   const baseBitmapRef = useRef(null); // ImageBitmap/HTMLImageElement de base

//   // Helpers: generar bitmap desde blob (rápido con createImageBitmap; fallback a <img>)
//   async function ensureBaseBitmapFromBlob(blob) {
//     if (!blob) return;
//     try {
//       if ("createImageBitmap" in window) {
//         baseBitmapRef.current = await createImageBitmap(blob);
//         return;
//       }
//     } catch {
//       // fallback continúa
//     }

//     const objUrl = URL.createObjectURL(blob);
//     await new Promise((resolve, reject) => {
//       const img = new Image();
//       img.onload = () => {
//         baseBitmapRef.current = img;
//         URL.revokeObjectURL(objUrl);
//         resolve();
//       };
//       img.onerror = (e) => {
//         URL.revokeObjectURL(objUrl);
//         reject(e);
//       };
//       img.src = objUrl;
//     });
//   }

//   /**
//    * Dibuja SIEMPRE desde baseBitmap -> canvas aplicando la rotación y
//    * actualiza preview + lastBlob (lo que realmente se subirá).
//    */
//   async function renderFromBaseAndUpdate(rotationDeg) {
//     const bmp = baseBitmapRef.current;
//     if (!bmp || !canvasRef.current) return;

//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext("2d");

//     const srcW = bmp.width;
//     const srcH = bmp.height;

//     const isQuarterTurn = rotationDeg % 180 !== 0;
//     const outW = isQuarterTurn ? srcH : srcW;
//     const outH = isQuarterTurn ? srcW : srcH;

//     canvas.width = outW;
//     canvas.height = outH;

//     ctx.save();
//     switch (rotationDeg % 360) {
//       case 0:
//         break;
//       case 90:
//         ctx.translate(outW, 0);
//         ctx.rotate(Math.PI / 2);
//         break;
//       case 180:
//         ctx.translate(outW, outH);
//         ctx.rotate(Math.PI);
//         break;
//       case 270:
//         ctx.translate(0, outH);
//         ctx.rotate((3 * Math.PI) / 2);
//         break;
//       default:
//         break;
//     }

//     ctx.drawImage(bmp, 0, 0);
//     ctx.restore();

//     const rotatedBlob = await new Promise((resolve, reject) => {
//       canvas.toBlob(
//         (b) =>
//           b
//             ? resolve(b)
//             : reject(new Error("No se pudo generar la imagen rotada")),
//         "image/jpeg",
//         0.92
//       );
//     });

//     const objUrl = URL.createObjectURL(rotatedBlob);
//     // revocar url anterior si existía
//     if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
//     previewUrlRef.current = objUrl;
//     setPreviewUrl(objUrl);
//     setLastBlob(rotatedBlob);
//   }

//   const SelectSupportMessage = async (type) => {
//     try {
//       setSupportMessageSelected(type);
//     } catch (error) {
//       // noop
//     }
//   };

//   // 1) Iniciar cámara al montar
//   useEffect(() => {
//     let stream;

//     const StartCamera = async () => {
//       setErrorMsg(null);
//       setIsStarting(true);

//       try {
//         // facingMode 'user' busca la cámara frontal; requiere HTTPS en producción
//         stream = await navigator.mediaDevices.getUserMedia({
//           video: { facingMode: "user" },
//           audio: false,
//         });

//         if (videoRef.current) {
//           videoRef.current.srcObject = stream;
//           // Algunos navegadores requieren muted para autoplay
//           videoRef.current.muted = true;
//           await videoRef.current.play();
//         }
//       } catch (err) {
//         console.error("Error al acceder a la cámara:", err);
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text:
//             "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos de tu navegador para darnos acceso.",
//         });
//         setErrorMsg(
//           "¡Ups! No ha sido posible acceder a la cámara. Revisa y autoriza los permisos de tu navegador para darnos acceso. Error: " +
//             err.message
//         );
//       } finally {
//         setIsStarting(false);
//       }
//     };

//     StartCamera();

//     // Cleanup: detener la cámara al desmontar
//     return () => {
//       if (stream) {
//         stream.getTracks().forEach((t) => t.stop());
//       }
//       if (previewUrlRef.current) {
//         URL.revokeObjectURL(previewUrlRef.current);
//         previewUrlRef.current = null;
//       }
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // 2) Capturar foto desde el video a canvas → Blob (base) y preparar preview rotatable
//   const handleCapture = useCallback(async () => {
//     if (!videoRef.current || !canvasRef.current) return;

//     setSelfieFanShooted(false);
//     setIsCapturing(true);
//     setErrorMsg(null);

//     try {
//       const video = videoRef.current;
//       const canvas = canvasRef.current;
//       const ctx = canvas.getContext("2d");

//       // Medidas del frame original
//       const vw = video.videoWidth;
//       const vh = video.videoHeight;
//       if (!vw || !vh) {
//         showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text:
//             "La cámara aún no está lista para capturar. Acepta el permiso de la cámara o dame un momento para alistarla.",
//         });
//         throw new Error(
//           "La cámara aún no está lista para capturar. Acepta el permiso de la cámara o dame un momento para alistarla."
//         );
//       }

//       // Redimensionado proporcional para limitar tamaño
//       let targetW = vw;
//       let targetH = vh;
//       const ratio = vw / vh;
//       if (targetW > MAX_WIDTH) {
//         targetW = MAX_WIDTH;
//         targetH = Math.round(MAX_WIDTH / ratio);
//       }
//       if (targetH > MAX_HEIGHT) {
//         targetH = MAX_HEIGHT;
//         targetW = Math.round(MAX_HEIGHT * ratio);
//       }

//       canvas.width = targetW;
//       canvas.height = targetH;

//       // Espejo para selfie en la foto resultante (si no lo quieres, comenta las 3 líneas save/translate/scale y restore)
//       ctx.save();
//       ctx.translate(targetW, 0);
//       ctx.scale(-1, 1);

//       ctx.drawImage(video, 0, 0, targetW, targetH);

//       ctx.restore();

//       // Generar Blob base (original de la captura)
//       const blob = await new Promise((resolve, reject) => {
//         canvas.toBlob(
//           (b) =>
//             b
//               ? resolve(b)
//               : reject(
//                   new Error(
//                     "Hubo un error al mostrar la vista previa de tu foto. Inténtalo nuevamente o inténtalo más tarde."
//                   )
//                 ),
//           "image/jpeg",
//           0.92
//         );
//       });

//       // Guardar como base para giros y preparar preview (rotación 0)
//       setBaseBlob(blob);
//       await ensureBaseBitmapFromBlob(blob);
//       setRotation(0);
//       await renderFromBaseAndUpdate(0);

//       setSelfieFanShooted(true);
//     } catch (err) {
//       console.error(err);
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text:
//           err.message ||
//           "Lo sentimos, no se pudo capturar la foto correctamente. Inténtalo nuevamente o inténtalo más tarde.",
//       });
//     } finally {
//       setIsCapturing(false);
//     }
//   }, [showMessage]);

//   // 3) Subir foto llamando a la BL (que a su vez usa la DL)
//   const handleUpload = useCallback(async () => {
//     if (!lastBlob) {
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: "¡Ups! Debes tomar una foto antes de intentar enviarla",
//       });
//       setErrorMsg("¡Ups! Debes tomar una foto antes de intentar enviarla");
//       return;
//     }
//     setIsUploading(true);
//     setErrorMsg(null);

//     try {
//       const supportMessage =
//         SUPPORT_MESSAGE_BY_ID[supportMessageSelected] ||
//         DEFAULT_SUPPORT_MESSAGE;

//       const selfieFanData = {
//         blob: lastBlob,
//         mode: "binary", // "multipart" | "binary"
//         caption: supportMessage,
//       };
//       debugger
//       const response = await BLUploadSelfie(selfieFanData);
//       debugger
//       if (response.success) {
//         showMessage({
//           type: MESSAGE_TYPE.SUCCESS,
//           text: "¡Muchas gracias! Tu foto Selfie Fan ha sido enviada con éxito",
//         });
//       } else {
//         console.log(response.message);
//       }
//     } catch (err) {
//       console.error(err);
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text:
//           "Lo sentimos. Ha habido un error al intentar enviar tu foto. Inténtalo nuevamente o inténtalo más tarde.",
//       });
//       setErrorMsg(
//         err.message ||
//           "Lo sentimos. Ha habido un error al intentar enviar tu foto. Inténtalo nuevamente o inténtalo más tarde."
//       );
//     } finally {
//       setIsUploading(false);
//     }
//   }, [lastBlob, supportMessageSelected, showMessage]);

//   return (
//     <div className="SelfieFan__Fan container">
//       <h1 className="SelfieFan__Fan__Title">Bienvenid@ Aficionad@</h1>
//       <p className="SelfieFan__Fan__Text--Center">¡Apoyemos a nuestro equipo!</p>

//       <ol>
//         <li>Tómate una Selfie Fan</li>
//         <li>Elige el mensaje de apoyo que más te guste</li>
//         <li>Da clic en el botón Enviar mi mensaje de apoyo</li>
//       </ol>

//       <p className="SelfieFan__Fan__Text--Center">
//         Tu Selfie Fan podría aparecer en pantalla mostrando tu apoyo al equipo
//       </p>

//       <div className="SelfieFan__Fan__Video">
//         {/* muted ya se fuerza en código para autoplay; puedes mantener aquí también */}
//         <video
//           ref={videoRef}
//           className="SelfieFan__Fan__Video__Screen"
//           autoPlay
//           playsInline
//           muted
//         />
//       </div>

//       <div className="SelfieFan__Fan__Video__Buttons">
//         <button
//           onClick={handleCapture}
//           className={`${
//             selfieFanShooted ? "Btn__Adsl--RedOutline" : "Btn__Adsl--Red"
//           } m-auto mt-3`}
//           disabled={isStarting || isCapturing}
//         >
//           {isCapturing ? (
//             "Capturando..."
//           ) : (
//             <>
//               <i className="bi bi-camera"></i>{" "}
//               {selfieFanShooted ? "Tomar otra foto" : "Tomar foto"}
//             </>
//           )}
//         </button>

//         <p className="SelfieFan__Fan__Video__SupportMessages__Title">
//           Selecciona un mensaje de apoyo para acompañar tu foto Selfie Fan:
//         </p>
//         <ul className="SelfieFan__Fan__Video__SupportMessages">
//           <li
//             className={`${
//               supportMessageSelected === supportMessages.ONE
//                 ? "Btn__Adsl--Red"
//                 : "Btn__Adsl--RedOutline"
//             } SelfieFan__Fan__Video__SupportMessages__Item`}
//             onClick={() => SelectSupportMessage(supportMessages.ONE)}
//           >
//             Vamos con todo equipo
//           </li>
//           <li
//             className={`${
//               supportMessageSelected === supportMessages.TWO
//                 ? "Btn__Adsl--Red"
//                 : "Btn__Adsl--RedOutline"
//             } SelfieFan__Fan__Video__SupportMessages__Item`}
//             onClick={() => SelectSupportMessage(supportMessages.TWO)}
//           >
//             Viva el San Luis
//           </li>
//           <li
//             className={`${
//               supportMessageSelected === supportMessages.THREE
//                 ? "Btn__Adsl--Red"
//                 : "Btn__Adsl--RedOutline"
//             } SelfieFan__Fan__Video__SupportMessages__Item`}
//             onClick={() => SelectSupportMessage(supportMessages.THREE)}
//           >
//             Arriba las Auriazules
//           </li>
//         </ul>
//       </div>

//       {/* Oculto; solo para procesar el frame y giros */}
//       <canvas ref={canvasRef} className="d-none" />

//       {previewUrl && (
//         <div className="mt-5">
//           <p className="text-center">Vista previa:</p>
//           <img
//             src={previewUrl}
//             alt="Tu foto aparecerá aquí"
//             style={{ width: "100%", maxWidth: 1024, maxHeight: 768, borderRadius: 8, margin: "auto", display: "block" }}
//           />

//           {/* Controles de giro */}
//           <div className="flex gap-2 justify-center mt-3">
//             <button
//               type="button"
//               className="Btn__Adsl--RedOutline m-auto"
//               disabled={!baseBlob || isCapturing}
//               onClick={async () => {
//                 const newRot = (rotation + 90) % 360;
//                 setRotation(newRot);
//                 await renderFromBaseAndUpdate(newRot);
//               }}
//             >
//               Girar 90° ↻
//             </button>

//             <button
//               type="button"
//               className="Btn__Adsl--RedOutline m-auto mt-4"
//               disabled={!baseBlob || isCapturing || rotation === 0}
//               onClick={async () => {
//                 setRotation(0);
//                 await renderFromBaseAndUpdate(0);
//               }}
//             >
//               Restaurar
//             </button>
//           </div>

//           {/* Texto del mensaje elegido debajo de la imagen */}
//           {supportMessageSelected ? (
//             supportMessageSelected === supportMessages.ONE ? (
//               <p className="SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//                 Vamos con todo equipo
//               </p>
//             ) : supportMessageSelected === supportMessages.TWO ? (
//               <p className="SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//                 Viva el San Luis
//               </p>
//             ) : supportMessageSelected === supportMessages.THREE ? (
//               <p className="SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//                 Arriba las Auriazules
//               </p>
//             ) : (
//               <p className="SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//                 {DEFAULT_SUPPORT_MESSAGE}
//               </p>
//             )
//           ) : (
//             <p className="SelfieFan__Fan__Text--Center SelfieFan__Fan__Text--SpaceUp">
//               {DEFAULT_SUPPORT_MESSAGE}
//             </p>
//           )}
//         </div>
//       )}

//       {selfieFanShooted && (
//         <button
//           onClick={handleUpload}
//           className="Btn__Adsl--Red m-auto mt-3"
//           disabled={!lastBlob || isUploading}
//         >
//           {isUploading ? "Subiendo Selfie Fan..." : "Enviar mi mensaje de apoyo"}
//         </button>
//       )}

//       {errorMsg && (
//         <p className="text-center mt-4" style={{ color: "crimson", marginTop: 8 }}>
//           <strong>Error:</strong> {errorMsg}
//         </p>
//       )}
//     </div>
//   );
// }

// export { SelfieFan };